import { useEffect, useState } from "react"
import { useGame } from "@/hooks/useGame"
import { useGameMoves } from "@/hooks/useGameMoves"
import formatDate from "@/utils/formatDate"
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react"
import { Cog8ToothIcon } from "@heroicons/react/20/solid"
import clsx from "clsx"

const annotations = ["!", "?", "!!", "??", "!?", "?!"]

const Annotations = ({
  isOwner,
  compact = false,
}: {
  isOwner: boolean
  compact?: boolean
}) => {
  const [comment, setComment] = useState("")
  const { initialGame, isDraft } = useGame()
  const { addAnnotation, addComment, currentMove, resetToMove, undoMove } =
    useGameMoves()

  useEffect(() => {
    if (!currentMove) return
    setComment(currentMove?.comment || "")
  }, [currentMove])

  if (!currentMove && isDraft) return null

  if (!currentMove) {
    return (
      <dl
        className={clsx(
          "overflow-y-auto border-t border-gray-200",
          compact ? "p-4 lg:p-6" : "p-6"
        )}
      >
        {initialGame.date ? (
          <>
            <dt className="sr-only">Date</dt>
            <dd>
              <time
                dateTime={initialGame.date}
                className="text-chessdrop-500 text-sm font-medium tracking-tight uppercase"
              >
                {formatDate({
                  date: initialGame.date,
                  format: "long",
                })}
              </time>
            </dd>
          </>
        ) : null}
        {initialGame.event ? (
          <>
            <dt className="sr-only">Event</dt>
            <dd className="mt-1 text-sm font-medium tracking-tight text-gray-700">
              {initialGame.event.name}
            </dd>
          </>
        ) : null}
        {initialGame.event_round || initialGame.board ? (
          <div className="mt-1">
            {initialGame.event_round ? (
              <span className="text-sm font-medium tracking-tight text-gray-500">
                <dt className="inline">Round</dt>
                <dd className="inline"> {initialGame.event_round}</dd>
              </span>
            ) : null}
            {initialGame.board ? (
              <span className="text-sm font-medium tracking-tight text-gray-500">
                {initialGame.event_round ? ", " : ""}
                <dt className="inline">Board</dt>
                <dd className="inline"> {initialGame.board}</dd>
              </span>
            ) : null}
          </div>
        ) : null}
        {initialGame.description ? (
          <>
            <dt className="sr-only">Description</dt>
            <dd className="mt-5 text-sm font-medium tracking-tight whitespace-pre-wrap text-gray-500">
              {initialGame.description}
            </dd>
          </>
        ) : null}
      </dl>
    )
  }

  return isOwner ? (
    <div className="flex justify-between gap-x-6 border-t border-gray-200 p-6">
      <div className="flex-1">
        <div>
          <label htmlFor="comment" className="sr-only">
            Move comment
          </label>
          <textarea
            rows={2}
            name="comment"
            id="comment"
            className="focus:ring-chessdrop-600 block w-full rounded-xl border-0 py-1.5 text-gray-900 ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6"
            placeholder="Add a comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onBlur={(e) => addComment(e.target.value)}
          />
        </div>
      </div>
      <div className="flex gap-x-2">
        <div>
          <label htmlFor="annotation" className="sr-only">
            Move annotation
          </label>
          <select
            id="annotation"
            name="annotation"
            className="focus:ring-chessdrop-600 block rounded-xl border-0 py-1.5 pr-8 pl-3 text-gray-900 ring-1 ring-gray-300 ring-inset focus:ring-2 sm:text-sm sm:leading-6"
            value={currentMove?.annotation || ""}
            onChange={(e) => addAnnotation(e.target.value)}
          >
            <option></option>
            {annotations.map((annotation) => (
              <option key={annotation}>{annotation}</option>
            ))}
          </select>
        </div>
        <Menu as="div" className="relative inline-block text-left">
          <div>
            <MenuButton className="focus:ring-chessdrop-600 block rounded-xl border-0 bg-white px-3 py-2.5 text-gray-900 ring-1 ring-gray-300 ring-inset focus:ring-2 sm:text-sm sm:leading-6">
              <span className="sr-only">Edit</span>
              <Cog8ToothIcon
                className="h-4 w-4 text-gray-600"
                aria-hidden="true"
              />
            </MenuButton>
          </div>

          <MenuItems
            className={clsx([
              "absolute right-0 bottom-full z-10 mt-2 w-40 origin-bottom-right -translate-y-2 overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-black/5 focus:outline-none lg:bottom-auto lg:mt-4 lg:origin-top-right",
              "data-closed:scale-95 data-closed:opacity-0",
              "data-enter:duration-100 data-enter:ease-out",
              "data-leave:duration-75 data-leave:ease-in",
            ])}
          >
            <div className="py-2">
              <MenuItem>
                <button
                  onClick={resetToMove}
                  className="block w-full px-4 py-3 text-left text-sm text-gray-700 disabled:opacity-30 data-focus:bg-gray-100 data-focus:text-gray-900"
                >
                  Reset to this move
                </button>
              </MenuItem>
              <MenuItem>
                <button
                  onClick={undoMove}
                  className="block w-full px-4 py-3 text-left text-sm text-gray-700 disabled:opacity-30 data-focus:bg-gray-100 data-focus:text-gray-900"
                >
                  Undo last move
                </button>
              </MenuItem>
            </div>
          </MenuItems>
        </Menu>
      </div>
    </div>
  ) : (
    <div className="border-t border-gray-200 p-6 text-sm font-medium tracking-tight whitespace-pre-wrap text-gray-500">
      {comment}
    </div>
  )
}

export default Annotations
